'use client'
import { SbBlokData, storyblokEditable, StoryblokServerComponent } from '@storyblok/react/rsc'
import { FC, useEffect, useState } from 'react'

import { PhnxAvatar } from '@/components/01-atoms/Avatar/Avatar'
import { PhnxImageHolder } from '@/components/01-atoms/ImageHolder/ImageHolder'
import { PhnxAuthorcard } from '@/components/02-molecules/Authorcard/Authorcard'
import { PhnxCard } from '@/components/02-molecules/Card/Card'
import { searchParamsData } from '@/lib/sbAPI'
import { getStoryblokApi } from '@/lib/storyblok'

import { PhnxAdvisor } from './Advisor'
import { AdvisorProps } from './Advisor'

interface AdvisorBlokProps {
  blok: {
    image: any
    title: AdvisorProps['title']
    bio: SbBlokData[]
    specialities: SbBlokData[]
    offices: SbBlokData[]
    salesforceid: string
  } & SbBlokData
  name: string
  uuid: string
}

const Advisor: FC<AdvisorBlokProps> = ({ blok, name, uuid }) => {
  const [specialties, setSpecialties] = useState<any[]>([])
  const [offices, setOffices] = useState<any[]>([])
  const [articles, setArticles] = useState<any[]>([])
  const [specialtyLinks, setSpecialtyLinks] = useState<any[]>([])

  // Fetch the service lines links from the datasource
  useEffect(() => {
    const fetchSpecialtyLinks = async () => {
      const storyblokApi = getStoryblokApi()
      const { data } = await storyblokApi.get(`cdn/datasource_entries`, {
        ...searchParamsData,
        per_page: 100,
        datasource: 'service-lines-links',
      })
      setSpecialtyLinks(data.datasource_entries)
    }
    fetchSpecialtyLinks()
  }, [])

  // Combine the specialties with the links
  useEffect(() => {
    if (blok.specialities && blok.specialities.length > 0 && specialtyLinks.length > 0) {
      const combinedSpecialties = blok.specialities.map((item: any) => {
        const matchingLink = specialtyLinks.find((link: any) => link.name === item)
        return {
          title: item,
          href:
            matchingLink && matchingLink.value.includes('/')
              ? matchingLink.value
              : `/advisers/search?specialty=${item}`,
        }
      })
      setSpecialties(combinedSpecialties)
    }
  }, [blok.specialities, specialtyLinks])

  useEffect(() => {
    let offices: any[] = []

    if (blok.offices && blok.offices.length > 0) {
      blok.offices.forEach((office: any) => {
        if (office && office.content && office.content.component === 'advanceOffice') {
          if (office.content.office && office.content.office[0]) {
            offices.push({
              phone: office.content.office[0].phone,
              address: [office.name, office.content.office[0].address],
              mapHref: `/locations/search?lat=${office.content.office[0].location?.[0]?.latitude || ''}&lon=${office.content.office[0].location?.[0]?.longitude || ''}`,
            })
          }
        } else if (office && office.content) {
          offices.push({
            phone: office.content.Phone,
            address: [
              office.name,
              office.content.address,
              [office.content.city, office.content.state, office.content.postcode]
                .filter(Boolean)
                .join(' '),
            ],
            mapHref: `/locations/search?lat=${office.content.Latitude || ''}&lon=${office.content.Longitude || ''}`,
          })
        }
      })
      setOffices(offices)
    }
  }, [blok.offices])

  useEffect(() => {
    // find articles wiith author of uuid
    // TODO: this should be reusable
    let location = process.env.NEXT_PUBLIC_NEXT_LOCATION
      ? process.env.NEXT_PUBLIC_NEXT_LOCATION
      : 'au'
    let filterLocation = location === 'en-nz' ? 'au' : `en-nz`

    const fetchArticles = async () => {
      let sbParams = {
        ...searchParamsData,
        filter_query: {
          component: { in: 'article' },
          author: { in: uuid },
          __or: [
            { 'settings.location_available': { in: location } },
            { 'settings.location_available': { not_in: filterLocation } },
          ],
        },
        sort_by: 'published_at:desc',
        per_page: 100,
      }

      const storyblokApi = getStoryblokApi()
      let { data } = await storyblokApi.get(`cdn/stories/`, sbParams)
      let results = []
      if (data?.stories) {
        results = data.stories.map((story: any) => {
          return {
            title: story.name,
            tags: story.content.tags,
            link: `/${story.full_slug}`,
            asset: {
              src: story.content.featured_image.filename,
              alt: story.content.featured_image.alt,
            },
          }
        })
      }
      setArticles(results)
    }
    fetchArticles()
  }, [uuid])

  return (
    <div {...storyblokEditable(blok)}>
      <PhnxAdvisor
        role={blok.title}
        title={name}
        avatar={
          blok.image && (
            <PhnxAvatar
              size="extralarge"
              style="square"
              asset={{ src: blok.image.filename, alt: blok.image.alt }}
            />
          )
        }
        biography={
          blok.bio &&
          blok.bio.map((child: SbBlokData) => (
            <StoryblokServerComponent blok={child} key={child._uid} />
          ))
        }
        specialties={specialties}
        officelocations={offices}
        link={'/advisers/search'}
        articles={
          articles && articles.length > 0
            ? articles.map((article, index) => (
                <PhnxCard
                  key={index}
                  variant="secondary"
                  layout="vertical"
                  alignment="default"
                  style="square"
                  feature={false}
                  asset={
                    <PhnxImageHolder
                      images={[{ src: article.asset.src, alt: article.asset.alt, focus: '' }]}
                    />
                  }
                  title={article.title}
                  tags={article.tags}
                  author={
                    <PhnxAuthorcard
                      layout="compact"
                      name={name ?? 'By Findex Contributor'}
                      asset={{
                        src: blok.image.filename ?? '/assets/findex_contributer.png',
                        alt: blok.image.alt ?? 'Findex Contributor',
                      }}
                    />
                  }
                  link={article.link}
                />
              ))
            : null
        }
        salesforceid={blok.salesforceid}
      />
    </div>
  )
}

export default Advisor
